import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useMyProfile } from '../session/useMyProfile.ts'
import { servicesKeys, usePlan, type Plan } from './useServices.ts'

/**
 * One-tap reasons for Reject: the scheduler gets a word without
 * anyone having to type on a phone. A note can go with one, or instead.
 */
export const DECLINE_REASONS = ['Away', 'Working', 'Sick', 'Family'] as const
export type DeclineReason = (typeof DECLINE_REASONS)[number]

/** What the scheduler reads: "Away — back on the 3rd", "Away", or just the note. */
export function declineReason(quick: DeclineReason | null, note: string): string {
  const text = note.trim()
  if (quick && text) return `${quick} — ${text}`
  return quick ?? text
}

/**
 * My Schedule: the requests to serve that are mine, a one-tap answer to each,
 * and the dates I am away. Any member — it is only ever their own rows.
 */
export function useMySchedule() {
  const api = useApi()
  const queryClient = useQueryClient()
  const schedule = useQuery({
    queryKey: servicesKeys.mine(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.me.schedule.get()
      if (error) throw error
      return data
    },
  })
  const put = (data: unknown) => {
    if (data) queryClient.setQueryData(servicesKeys.mine(), data)
  }
  type Schedule = NonNullable<typeof schedule.data>
  /**
   * The answer shows at once — accepted moves to "You're serving", declined
   * leaves "Waiting" — and goes back if the server refuses it.
   */
  const respond = useMutation({
    mutationFn: async ({
      id,
      accept,
      reason,
    }: {
      id: string
      accept: boolean
      reason?: string | null
    }) => {
      const { data, error } = await api.api.v1.me
        .schedule({ id })
        .respond.post({ accept, reason: reason ?? null })
      if (error) throw error
      return data
    },
    onMutate: async ({ id, accept }) => {
      await queryClient.cancelQueries({ queryKey: servicesKeys.mine() })
      const previous = queryClient.getQueryData<Schedule>(servicesKeys.mine())
      if (previous)
        queryClient.setQueryData<Schedule>(servicesKeys.mine(), {
          ...previous,
          requests: previous.requests.map((r) =>
            r.id === id ? { ...r, status: accept ? 'accepted' : 'declined' } : r,
          ),
        })
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(servicesKeys.mine(), context.previous)
    },
    onSuccess: put,
  })
  const addBlockout = useMutation({
    mutationFn: async (body: {
      startsOn: string
      endsOn: string
      reason?: string | null
      /** Comes round again: `BLOCKOUT_REPEATS` in @church/domain, with an optional end. */
      rule?: { freq: 'DAILY' | 'WEEKLY' | 'MONTHLY'; interval?: number; until?: string } | null
    }) => {
      const { data, error } = await api.api.v1.services.blockouts.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const removeBlockout = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.me.schedule.blockouts({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const requests = schedule.data?.requests ?? []
  return {
    linked: schedule.data?.personId !== null,
    isLoading: schedule.isPending,
    error: schedule.error,
    refetch: schedule.refetch,
    waiting: requests.filter((r) => r.status === 'requested'),
    upcoming: requests.filter((r) => r.status === 'accepted'),
    declined: requests.filter((r) => r.status === 'declined'),
    blockouts: schedule.data?.blockouts ?? [],
    respond,
    addBlockout,
    removeBlockout,
  }
}

/**
 * The signed-in person's own places on one plan, answered from the plan
 * itself: what they were asked to do and not yet answered (`asked`), and
 * what they said yes to (`serving`). Answering takes every asked place at
 * once — "You're scheduled as Keys and Vocals" is one question. Declining
 * needs a word for the scheduler, as on My Schedule.
 */
export function useMyPlanPlaces(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const plan = usePlan(planId).data
  const me = useMyProfile().data
  const mine = plan && me ? plan.people.filter((p) => p.personId === me.id) : []
  const asked = mine.filter((p) => p.status === 'requested')
  const respond = useMutation({
    // The answer shows on the plan at once, and goes back if refused.
    onMutate: async ({ accept }: { accept: boolean; reason?: string | null }) => {
      const key = servicesKeys.plan(planId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Plan>(key)
      const ids = new Set(asked.map((p) => p.id))
      if (previous)
        queryClient.setQueryData<Plan>(key, {
          ...previous,
          people: previous.people.map((p) =>
            ids.has(p.id) ? { ...p, status: accept ? 'accepted' : 'declined' } : p,
          ),
        })
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(servicesKeys.plan(planId), context.previous)
    },
    mutationFn: async ({ accept, reason }: { accept: boolean; reason?: string | null }) => {
      let latest: unknown = null
      for (const place of asked) {
        const { data, error } = await api.api.v1.me
          .schedule({ id: place.id })
          .respond.post({ accept, reason: reason ?? null })
        if (error) throw error
        latest = data
      }
      return latest
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(servicesKeys.mine(), data)
      void queryClient.invalidateQueries({ queryKey: servicesKeys.plan(planId) })
    },
  })
  return {
    asked,
    serving: mine.filter((p) => p.status === 'accepted'),
    respond,
  }
}
